import { AuditLogChange } from '../types';

export type FormattedAuditLogValue = {
  /**
   * String representation of the value ready to be rendered in the UI.
   */
  displayValue: string;
  /**
   * Indicates if the original value contains meaningful content (not null/undefined/empty).
   */
  hasValue: boolean;
  /**
   * Used to decide whether typography should allow multi-line rendering.
   */
  isMultiline: boolean;
};

const EMPTY_VALUE = '—';

export function formatAuditLogChangeValue(value: unknown): FormattedAuditLogValue {
  if (value === null || value === undefined) {
    return { displayValue: EMPTY_VALUE, hasValue: false, isMultiline: false };
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (!trimmed.length) {
      return { displayValue: EMPTY_VALUE, hasValue: false, isMultiline: false };
    }

    return {
      displayValue: value,
      hasValue: true,
      isMultiline: value.includes('\n'),
    };
  }

  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return { displayValue: String(value), hasValue: true, isMultiline: false };
  }

  try {
    const json = JSON.stringify(value, null, 2);
    return {
      displayValue: json,
      hasValue: true,
      isMultiline: json.includes('\n'),
    };
  } catch (error) {
    return {
      displayValue: String(value),
      hasValue: true,
      isMultiline: false,
    };
  }
}

export function summarizeAuditLogChanges(changes: AuditLogChange[]): string[] {
  return changes
    .map(({ field, from, to }) => {
      const hasFrom = from !== undefined;
      const hasTo = to !== undefined;
      const fromFormatted = hasFrom ? formatAuditLogChangeValue(from) : null;
      const toFormatted = hasTo ? formatAuditLogChangeValue(to) : null;

      if (
        hasFrom &&
        hasTo &&
        fromFormatted &&
        toFormatted &&
        fromFormatted.displayValue !== toFormatted.displayValue
      ) {
        return `${field}: ${fromFormatted.displayValue} → ${toFormatted.displayValue}`;
      }

      if (hasTo && toFormatted) {
        return `${field}: ${toFormatted.displayValue}`;
      }

      if (hasFrom && fromFormatted) {
        return `${field}: ${fromFormatted.displayValue}`;
      }

      return field;
    })
    .filter((summary): summary is string => Boolean(summary));
}
